import { ChartDataOptionsBuilder } from './chart-data-options-builder';
import {
  createDefaultChartDataOptionsBuilder,
  DefaultChartDataOptionsBuilder
} from './default-chart-data-options-builder';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import { FavouriteDataOptions } from '../common/favourite-data-options';
import { FavouriteRowValue } from '../common/favourite-interfaces';
import { DrillDownHandler } from '../common/drill-down-builder';
import { escapeHtml } from '../common/utils';
import { SeriesTypeColumnMappings } from 'app/components/favourite-layout/favourite/chart-data-options/types';
import partial from 'lodash/partial';
export class PieChartDataOptionsBuilder implements ChartDataOptionsBuilder {

  private readonly DEFAULT_ICON_DIMENSIONS: number = 16;
  private dataAdapter: FavouriteDataAdapter;
  private columnMappings: SeriesTypeColumnMappings = {
    mainSeries: {
      sel1: 'x',
      sel2: 'splitting',
      aggregation: 'y'
    }
  };

  private chartDataOptionsBuilder: DefaultChartDataOptionsBuilder;

  constructor(dataOptions: FavouriteDataOptions, dataAdapter: FavouriteDataAdapter, drillDownHandler: DrillDownHandler) {
    this.dataAdapter = dataAdapter;

    this.dataAdapter.hasTarget = () => false; // Not supported for pie
    this.dataAdapter.hasRollingAverage = () => false; // Not supported for pie
    this.dataAdapter.showPercentageInTooltip = true;

    this.chartDataOptionsBuilder = createDefaultChartDataOptionsBuilder(dataOptions, dataAdapter, drillDownHandler, this.columnMappings);
    this.chartDataOptionsBuilder.isLegendEnabled = () => true;
    this.chartDataOptionsBuilder.useLegendTitle = () => true;
  }

  public setOptions(options: Highcharts.Options, splitRow: FavouriteRowValue, seriesValues: FavouriteRowValue[]) {
    this.chartDataOptionsBuilder.setOptions(options, splitRow, seriesValues);
    this.defineAggregationValueFormatter(options);
    this.setViewExtraAttributes();
  }

  public colIndexOf(typeName: string, seriesType: string) {
    return this.chartDataOptionsBuilder.colIndexOf(typeName, seriesType);
  }

  public isXAxisReversedByDefault() {
    return this.chartDataOptionsBuilder.isXAxisReversedByDefault();
  }

  private setViewExtraAttributes() {
    const layoutAttributes = this.dataAdapter.layoutConfig.layoutAttributes;
    if (layoutAttributes.Pie?.DonutView) {
      layoutAttributes.Highcharts.plotOptions.series.innerSize = '50%';
    }
    if (layoutAttributes.Pie?.SemiCircleView) {
      layoutAttributes.Highcharts.plotOptions.series.startAngle = -90;
      layoutAttributes.Highcharts.plotOptions.series.endAngle = 90;
      layoutAttributes.Highcharts.plotOptions.series.center = ['50%', '75%'];
    }
  }

  private defineAggregationValueFormatter(options: Highcharts.Options): void {

    // The values inside the pie
    options.plotOptions.pie = options.plotOptions.pie || {};
    options.plotOptions.pie.dataLabels = options.plotOptions.pie.dataLabels || {};

    (options.plotOptions.pie.dataLabels as Highcharts.SeriesPieDataLabelsOptionsObject).format = undefined;

    const dataAdapter = this.dataAdapter;
    const percentageFormatter = dataAdapter.buildNumberFormatter(dataAdapter.intl, true, 1, 'percent');

    const chartDataOptionsBuilder = this.chartDataOptionsBuilder;
    const colIndexOf = (typeName: string, seriesType: string) => this.colIndexOf(typeName, seriesType);
    const isShowValueEnabled = this.chartDataOptionsBuilder.isShowValueEnabled();
    const isShowPercentEnabled = this.chartDataOptionsBuilder.isShowPercentEnabled();
    const isHideDatalabelsEnabled = this.chartDataOptionsBuilder.isHideDatalabelsEnabled();
    this.dataAdapter.layoutConfig.layoutAttributes.Highcharts.plotOptions.pie.dataLabels.enabled = !isHideDatalabelsEnabled;

    // eslint:disable-next-line: no-this-assignment
    const self = this;
    // eslint:disable
    (options.plotOptions.pie.dataLabels as Highcharts.SeriesPieDataLabelsOptionsObject).formatter = function () {
      const hidePointName = !!this.series.chart.options.plotOptions.pie.showInLegend;
      const label = hidePointName ? '' : escapeHtml(this.point.name) + ': ';
      const labelPointValue = isShowValueEnabled ? escapeHtml(dataAdapter.format(dataAdapter.aggrColIndex, this.point.y)) : '';
      const labelPointPercent = isShowPercentEnabled ? `${isShowValueEnabled ? ' (' : ''}${percentageFormatter(this.percentage)}${isShowValueEnabled ? ')' : ''}` : '';
      const labelValueText = `<b>${labelPointValue}${labelPointPercent}</b>`;
      const xColIndex = colIndexOf('x', 'mainSeries');
      if (chartDataOptionsBuilder.useHtml(xColIndex) && !hidePointName) {
        // try to find a category corresponding to the value, and check if it has an icon
        const found = dataAdapter.columnCategories[xColIndex].find(
          partial(
            (catName, xColInner, current) => {
              return catName === dataAdapter.format(xColInner, current.value || (current as any).Value);
            },
            this.point.name, xColIndex
          ));
        if (found) {
          const extraHtml = chartDataOptionsBuilder.createIconOrColourDiv(xColIndex, found, false);
          if (extraHtml.length > 0) {
            return extraHtml + ' ' + chartDataOptionsBuilder.addSpanWrapper(label + labelValueText, self.DEFAULT_ICON_DIMENSIONS);
          }
        }
      }
      return label + labelValueText;
    };
    // eslint:enable

    const xCol = colIndexOf('x', 'mainSeries');
    if (chartDataOptionsBuilder.useHtml(xCol)) { // Use HTML labels if we have icons to display
      (options.plotOptions.pie.dataLabels as Highcharts.SeriesPieDataLabelsOptionsObject).useHTML = true;
    }
  }
}
